import React from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import LockIcon from '@atlaskit/icon/core/lock-locked';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ReactRenderer, ADFEncoder } from '@atlaskit/renderer';
import { token } from '@atlaskit/tokens';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { AgentTextareaField } from '../../../../../common/ui/agent-textarea-field/index.tsx';

import messages from './messages.ts';

const styles = cssMap({
	contentWrapper: {
		boxShadow: token('elevation.shadow.raised'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		borderRadius: token('radius.small'),
	},
});

const adfEncoder = new ADFEncoder((schema) => new MarkdownTransformer(schema));

type AgentBehaviourProps = {
	agent: Agent;
	onCopy: (behaviour: string) => void;
	isForgeAgent: boolean;
	fullHeightText?: boolean;
};

export const AgentBehaviour = ({
	agent,
	onCopy,
	isForgeAgent,
	fullHeightText = true,
}: AgentBehaviourProps): JSX.Element => {
	const behaviour = agent.behaviour;
	const { formatMessage } = useIntl();

	if (isForgeAgent) {
		return (
			<Inline xcss={styles.contentWrapper} space="space.100">
				<LockIcon color="currentColor" spacing="spacious" label="" />
				<Stack space="space.100" alignBlock="start">
					<Text weight="bold">{formatMessage(messages.privateInstructionsTitle)}</Text>
					<Text size="small">{formatMessage(messages.privateInstructionsDescription)}</Text>
				</Stack>
			</Inline>
		);
	}

	if (fg('agent_studio_instructions_editor_enabled')) {
		if (!behaviour) {
			return (
				<Box xcss={styles.contentWrapper}>
					<Text color="color.text.subtlest">{formatMessage(messages.noBehaviourPlaceholder)}</Text>
				</Box>
			);
		}
		const adfDoc = adfEncoder.encode(behaviour);

		return (
			<Box xcss={styles.contentWrapper}>
				<ReactRenderer document={adfDoc} />
			</Box>
		);
	}

	return (
		<AgentTextareaField
			value={behaviour}
			onCopy={onCopy}
			fullHeightText={fullHeightText}
			noValuePlaceholder={formatMessage(messages.noBehaviourPlaceholder)}
		/>
	);
};
